import React, { useState, useMemo } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MONTHS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

// Galerie chronologique des photos de chantier (CR partagés + photos Drive du projet)
const ChantierGallery = ({ meetingReports = [], drivePhotos = [] }) => {
  const [lightbox, setLightbox] = useState(null);

  const groups = useMemo(() => {
    const items = [];
    meetingReports.forEach(cr => {
      (cr.photos || []).forEach(ph => items.push({
        key: ph.photo_id,
        src: `${API}/moe/photos/${ph.photo_id}`,
        caption: cr.title,
        date: cr.meeting_date || cr.created_at,
        kind: 'img',
      }));
    });
    drivePhotos.forEach(doc => items.push({
      key: doc.doc_id || doc.filename,
      href: doc.drive_url,
      caption: doc.filename,
      date: doc.created_at,
      kind: 'drive',
    }));
    items.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
    const byMonth = {};
    items.forEach(it => {
      const ym = (it.date || '').slice(0, 7) || 'inconnu';
      (byMonth[ym] = byMonth[ym] || []).push(it);
    });
    return Object.keys(byMonth).sort().reverse().map(ym => {
      const [y, m] = ym.split('-');
      const label = m ? `${MONTHS_FR[parseInt(m, 10) - 1]?.replace(/^./, c => c.toUpperCase())} ${y}` : 'Date inconnue';
      return { ym, label, items: byMonth[ym] };
    });
  }, [meetingReports, drivePhotos]);

  const total = groups.reduce((n, g) => n + g.items.length, 0);
  if (total === 0) return null;

  return (
    <div className="realease-card p-6 mb-6" data-testid="chantier-gallery">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-gray-900">Galerie chantier</h3>
        <span className="text-xs text-gray-400">{total} photo(s)</span>
      </div>
      <div className="space-y-5">
        {groups.map(g => (
          <div key={g.ym} data-testid={`gallery-month-${g.ym}`}>
            <p className="text-xs font-semibold uppercase tracking-wide text-[#13736D] mb-2">{g.label}</p>
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
              {g.items.map(it => it.kind === 'img' ? (
                <button key={it.key} onClick={() => setLightbox(it)}
                        className="relative group aspect-square overflow-hidden rounded-lg border border-gray-200"
                        data-testid={`gallery-photo-${it.key}`}>
                  <img src={it.src} alt={it.caption} loading="lazy"
                       className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" />
                  <span className="absolute inset-x-0 bottom-0 bg-black/55 text-white text-[10px] px-1.5 py-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">{it.caption}</span>
                </button>
              ) : (
                <a key={it.key} href={it.href} target="_blank" rel="noreferrer"
                   className="aspect-square rounded-lg border border-gray-200 bg-gray-50 flex flex-col items-center justify-center gap-1 p-2 hover:border-[#13736D] transition-colors"
                   data-testid={`gallery-drive-${it.key}`}>
                  <svg className="w-6 h-6 text-[#13736D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span className="text-[10px] text-gray-600 text-center line-clamp-2 break-all">{it.caption}</span>
                </a>
              ))}
            </div>
          </div>
        ))}
      </div>

      {lightbox && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
             onClick={() => setLightbox(null)} data-testid="gallery-lightbox">
          <div className="max-w-4xl max-h-full">
            <img src={lightbox.src} alt={lightbox.caption} className="max-h-[80vh] rounded-lg mx-auto" />
            <p className="text-white text-sm text-center mt-3">
              {lightbox.caption}
              {lightbox.date && <span className="text-white/60"> — {new Date(lightbox.date).toLocaleDateString('fr-FR')}</span>}
            </p>
            <button onClick={() => setLightbox(null)}
                    className="absolute top-4 right-4 text-white text-2xl leading-none w-10 h-10 rounded-full bg-white/10 hover:bg-white/20"
                    data-testid="gallery-lightbox-close">✕</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChantierGallery;
